import {
    PublicOrder, money, esc, postJson, retrieveOrder, setOrderToken, showConfirmModal, getCsrfToken,
    renderNotFound, renderOrderInfoCard, renderPartsCard, renderOrderHeading, renderShippingCard
} from "../Common/PublicOrder";
import { sfUrl } from '../../Storefront/StorefrontPaths';
import { BotGuard } from '../../BotProtection/BotGuard';

// The order's secret link token, mirrored from setOrderToken() for postGuarded() below.
let pageOrderToken = '';

function useOrderToken(token: string | null | undefined): void {
    pageOrderToken = token || '';
    setOrderToken(token);
}

/** postJson plus the bot protection headers (#37115). */
async function postGuarded(url: string, body: any, guard: GuardSlot): Promise<Response> {
    const headers: Record<string, string> = { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': getCsrfToken() };
    if (pageOrderToken) headers['X-Order-Token'] = pageOrderToken;
    Object.assign(headers, await guard.getHeaders());
    return fetch(url, { method: 'POST', headers, body: JSON.stringify(body) });
}

/**
 * #37115: one _BotGuard container rendered by the page (hidden in #lookup-bot-guards). The steps
 * are re-rendered with innerHTML, so each render moves the container into its form's
 * [data-bot-slot]; the widget is created on the first mount and re-run on later ones (moving
 * the iframe reloads it). A guard the page didn't render (form off, or a signed-in caller for
 * returns) is a no-op: getHeaders() resolves to {}.
 */
class GuardSlot {
    private guard: BotGuard | null = null;
    private readonly container: HTMLElement | null;

    constructor(form: string) {
        this.container = document.querySelector(`#lookup-bot-guards [data-bot-guard="${form}"]`) as HTMLElement | null;
    }

    mount(root: ParentNode): void {
        const slot = root.querySelector('[data-bot-slot]');
        if (!this.container || !slot) return;
        slot.appendChild(this.container);
        if (this.guard) this.guard.reset();
        else this.guard = new BotGuard(this.container);
    }

    getHeaders(): Promise<Record<string, string>> {
        return this.guard ? this.guard.getHeaders() : Promise.resolve({});
    }

    /** After every submit: a Turnstile token can be verified only once. */
    reset(): void {
        this.guard?.reset();
    }
}

/** The message of a Serenity error body (bot check, 429, validation), if any. */
function errorMessage(data: any): string {
    return data?.Error?.Message || '';
}

const CANCEL_WINDOW_HOURS = 3;

// Customer-facing "Cancel Order" button is hidden for now (per request) — the cancel
// flow/logic is kept intact so it can be re-enabled by flipping this to true.
const SHOW_CANCEL_BUTTON = false;

interface ReturnAnswerOption {
    Id: number;
    OptionText: string;
    Order: number;
}

interface ReturnQuestion {
    Id: number;
    Order: number;
    QuestionText: string;
    MultiAnswer: boolean;
    Answers: ReturnAnswerOption[];
}

interface RmaFieldValues {
    customerName: string;
    phone: string;
    email: string;
    purchaseDate: string;
    deliveredType: string;
}

function toDateInputValue(value: any): string {
    if (!value) return '';
    const d = new Date(value);
    if (isNaN(d.getTime())) return '';
    return `${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, '0')}-${d.getDate().toString().padStart(2, '0')}`;
}

type Step = 'search' | 'order' | 'pickParts' | 'rmaForm';

class LookupOrderView {
    private host: HTMLElement;
    private directOrderId: number | null;
    private entity: PublicOrder | null = null;
    private step: Step = 'search';
    private selectedPartIds = new Set<number>();
    private questions: ReturnQuestion[] = [];
    private rmaFieldValues: RmaFieldValues | null = null;
    private searchEmail = '';
    private searchOrderNumber = '';
    private lookupGuard = new GuardSlot('lookuporder');
    private returnGuard = new GuardSlot('return');

    // Opened from "Initiate Return" in My Account (?return=1): skip the order view and go
    // straight to picking parts, as long as the order can still be returned.
    private startReturn: boolean;

    constructor(host: HTMLElement, directOrderId: number | null, linkExpired = false, startReturn = false) {
        this.host = host;
        this.directOrderId = directOrderId;
        this.startReturn = startReturn;
        if (directOrderId) {
            this.loadDirect(directOrderId);
        } else {
            // #36939: an order link without a valid secret (e.g. from an older email) lands here.
            this.renderSearch(linkExpired ? 'For your security, please enter your email and order number to view this order.' : undefined);
        }
    }

    private async loadDirect(orderId: number): Promise<void> {
        this.host.innerHTML = `<div class="sf-pay-shell"><div class="sf-loading">Loading order…</div></div>`;
        const entity = await retrieveOrder(orderId);
        if (!entity) {
            this.host.innerHTML = renderNotFound(sfUrl('/account'), 'Back to My Account');
            return;
        }
        this.entity = entity;
        if (this.startReturn && this.canReturn()) {
            this.selectedPartIds.clear();
            this.step = 'pickParts';
            this.renderPickParts();
            return;
        }
        this.step = 'order';
        this.renderOrder();
    }

    // ---------- Step: search by email + order number ----------

    private renderSearch(error?: string): void {
        this.host.innerHTML = `
            <div class="sf-pay-shell sf-pay-shell-narrow">
                <div class="sf-pay-card">
                    <div class="sf-pay-card-head">Order Lookup</div>
                    <div class="sf-pay-form-body">
                        <p class="sf-pay-muted">Enter your email address and order number to view your order.</p>
                        <form id="sf-lookup-form" class="sf-license-form">
                            <label class="sf-field">
                                <input type="email" id="lookup-email" value="${esc(this.searchEmail)}" placeholder=" " required />
                                <span class="sf-field-label">Email Address</span>
                            </label>
                            <label class="sf-field">
                                <input type="text" id="lookup-order-number" value="${esc(this.searchOrderNumber)}" placeholder=" " required />
                                <span class="sf-field-label">Order Number</span>
                            </label>
                            <div data-bot-slot></div>
                            <div class="sf-license-error">${esc(error || '')}</div>
                            <button type="submit" class="sf-btn sf-pay-btn-pay" id="sf-lookup-submit">View Order</button>
                        </form>
                    </div>
                </div>
            </div>`;

        this.lookupGuard.mount(this.host);
        this.host.querySelector('#sf-lookup-form')?.addEventListener('submit', e => {
            e.preventDefault();
            this.submitSearch();
        });

        window.scrollTo(0, 0);
    }

    private isValidEmail(v: string): boolean {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
    }

    private async submitSearch(): Promise<void> {
        const email = (this.host.querySelector('#lookup-email') as HTMLInputElement)?.value?.trim() || '';
        const orderNumber = (this.host.querySelector('#lookup-order-number') as HTMLInputElement)?.value?.trim() || '';
        this.searchEmail = email;
        this.searchOrderNumber = orderNumber;

        if (!email || !orderNumber) {
            this.renderSearch('Email and Order Number are required!');
            return;
        }
        if (!this.isValidEmail(email)) {
            this.renderSearch('Email is not valid!');
            return;
        }

        const btn = this.host.querySelector('#sf-lookup-submit') as HTMLButtonElement;
        if (btn) { btn.disabled = true; btn.textContent = 'Searching…'; }

        try {
            const res = await postGuarded('/Services/Default/Order/GetOrderByEmailAndOrderNumber', { Email: email, OrderNumber: orderNumber }, this.lookupGuard);
            this.lookupGuard.reset();
            const data = await res.json();
            if (!res.ok || data?.IsError || !data?.Entity) {
                this.renderSearch(errorMessage(data) || data?.Message || 'Order not found. Please check your details and try again.');
                return;
            }
            this.entity = new PublicOrder(data.Entity);
            // Email + order number proves it's theirs — carry the order's token for the
            // follow-up calls (cancel, return) on this page.
            useOrderToken(this.entity.PublicAccessToken);
            this.step = 'order';
            this.renderOrder();
        } catch {
            this.lookupGuard.reset();
            this.renderSearch('Something went wrong. Please try again.');
        }
    }

    // ---------- Step: order view ----------

    private canCancel(): boolean {
        const entity = this.entity!;
        return !entity.IsCanceled && !entity.IsSubmitted && (entity.HoursSincePayment ?? 0) <= CANCEL_WINDOW_HOURS;
    }

    private canReturn(): boolean {
        const entity = this.entity!;
        return !entity.IsCanceled && entity.Status === 'Complete' && entity.activeParts().some(p => !p.IsReturned);
    }

    private renderOrder(): void {
        const entity = this.entity!;
        const canceled = entity.IsCanceled;
        const canCancel = this.canCancel();
        const canReturn = this.canReturn();
        const { subtotal, shipping, handling, tax, total } = entity.totals();
        const parts = entity.activeParts();
        const statusLabel = canceled ? 'CANCELED' : entity.Status === 'Complete' ? 'SHIPPED' : (entity.Status || 'IN PROGRESS').toUpperCase();
        const statusClass = canceled ? 'sf-pay-badge-canceled' : entity.Status === 'Complete' ? 'sf-pay-badge-paid' : 'sf-pay-badge-pending';
        const cancelNote = !canceled && !canCancel
            ? (entity.IsSubmitted
                ? 'This order has already been submitted and can no longer be canceled online.'
                : 'This order cannot be cancelled because it has been more than 3 hours since it was paid.')
            : '';

        this.host.innerHTML = `
            <div class="sf-pay-shell">
                <div class="sf-pay-layout">
                    <div class="sf-pay-main">
                        ${renderOrderHeading(entity, statusLabel, statusClass)}
                        ${canceled ? `<div class="sf-pay-alert sf-pay-alert-danger">This order has been canceled.</div>` : ''}
                        ${renderOrderInfoCard(entity)}
                        ${renderPartsCard(parts, false, undefined, entity.CustomerPerformsInsuranceWork)}
                        ${renderShippingCard(parts)}
                    </div>

                    <div class="sf-pay-summary">
                        <div class="sf-pay-card-head">Order Summary</div>
                        <div class="sf-pay-summary-body">
                            <div class="sf-pay-row"><span>Subtotal</span><span>${money(subtotal)}</span></div>
                            ${shipping ? `<div class="sf-pay-row"><span>Shipping</span><span>${money(shipping)}</span></div>` : ''}
                            ${handling ? `<div class="sf-pay-row"><span>Handling</span><span>${money(handling)}</span></div>` : ''}
                            <div class="sf-pay-row"><span>Tax</span><span>${money(tax)}</span></div>
                            <div class="sf-pay-summary-divider"></div>
                            <div class="sf-pay-row-total"><span>Total Amount</span><span>${money(total)}</span></div>
                            <div data-region="order-error" class="sf-pay-error"></div>
                            ${canReturn ? `<button type="button" class="sf-btn sf-pay-btn-pay" id="sf-start-return">Initiate Return</button>` : ''}
                            ${SHOW_CANCEL_BUTTON && canCancel ? `<button type="button" class="sf-btn sf-btn-danger" id="sf-cancel-order">Cancel Order</button>` : ''}
                            ${SHOW_CANCEL_BUTTON && cancelNote ? `<div class="sf-pay-muted-sm">${esc(cancelNote)}</div>` : ''}
                            <button type="button" class="sf-btn sf-btn-secondary sf-pay-btn-close" id="sf-order-back">${this.directOrderId ? 'Back to My Account' : 'New Search'}</button>
                        </div>
                    </div>
                </div>
            </div>`;

        this.host.querySelector('#sf-order-back')?.addEventListener('click', () => {
            if (this.directOrderId) {
                window.location.href = sfUrl('/account');
            } else {
                this.step = 'search';
                this.renderSearch();
            }
        });
        this.host.querySelector('#sf-start-return')?.addEventListener('click', () => {
            this.selectedPartIds.clear();
            this.step = 'pickParts';
            this.renderPickParts();
        });
        this.host.querySelector('#sf-cancel-order')?.addEventListener('click', () => this.cancelOrder());

        window.scrollTo(0, 0);
    }

    private setOrderError(msg: string): void {
        const el = this.host.querySelector('[data-region="order-error"]') as HTMLElement;
        if (el) el.textContent = msg;
    }

    private async cancelOrder(): Promise<void> {
        this.setOrderError('');
        if (!this.canCancel()) {
            this.setOrderError('This order can no longer be canceled.');
            return;
        }
        const confirmed = await showConfirmModal({
            title: 'Cancel this order?',
            message: 'Your cancellation will be processed by support and a refund will be issued.',
            confirmLabel: 'Cancel Order',
            cancelLabel: 'Keep Order'
        });
        if (!confirmed) return;

        const btn = this.host.querySelector('#sf-cancel-order') as HTMLButtonElement;
        if (btn) { btn.disabled = true; btn.textContent = 'Canceling…'; }

        try {
            const res = await postJson('/Services/Default/Order/CancelOrder', { EntityId: this.entity!.Id });
            const data = await res.json();
            if (!res.ok || data?.IsError) {
                this.setOrderError(data?.Message || 'Could not cancel this order. Please try again.');
                if (btn) { btn.disabled = false; btn.textContent = 'Cancel Order'; }
                return;
            }
            const refreshed = await retrieveOrder(this.entity!.Id);
            if (refreshed) this.entity = refreshed;
            this.renderOrder();
        } catch {
            this.setOrderError('Could not cancel this order. Please try again.');
            if (btn) { btn.disabled = false; btn.textContent = 'Cancel Order'; }
        }
    }

    // ---------- Step: pick parts to return ----------

    private renderPickParts(): void {
        const entity = this.entity!;
        const parts = entity.activeParts().filter(p => !p.IsReturned);
        const isInsurance = entity.CustomerPerformsInsuranceWork;

        const rows = parts.map(p => `
            <tr>
                <td class="sf-pay-select-cell"><input type="checkbox" class="sf-return-part-cb" aria-label="Return ${esc(p.displayName()) || esc(p.PartSku)}" value="${p.Id}" ${this.selectedPartIds.has(p.Id) ? 'checked' : ''} /></td>
                <td class="sf-pay-strong sf-pay-td-sku" data-label="SKU">${esc(p.PartSku)}</td>
                <td class="sf-pay-muted sf-pay-td-name">${esc(p.displayName()) || '—'}</td>
                <td class="sf-pay-td-oem" data-label="OEM">${esc(p.OemNumber) || '—'}</td>
                ${isInsurance ? `<td class="sf-pay-right" data-label="List Price">${money((!p.List ? p.PartListPrice : p.List) ?? 0)}</td>` : ''}
                <td class="sf-pay-right" data-label="${isInsurance ? 'Net' : 'Price'}">${money(p.ListPrice)}</td>
                <td class="sf-pay-right" data-label="Qty">${p.Quantity}</td>
                <td class="sf-pay-right sf-pay-strong" data-label="${isInsurance ? 'Net Total' : 'Total'}">${money(p.Total)}</td>
            </tr>`).join('');

        this.host.innerHTML = `
            <div class="sf-pay-shell">
                <div class="sf-pay-card">
                    <div class="sf-pay-card-head">Select Parts to Return — Order ${esc(entity.OrderId)}</div>
                    <div class="sf-pay-table-wrap">
                        <table class="sf-pay-table">
                            <thead>
                                <tr>
                                    <th></th><th>Part SKU</th><th>Part Name</th><th>OEM Number</th>
                                    ${isInsurance ? `<th class="sf-pay-right">List Price</th>` : ''}
                                    <th class="sf-pay-right">${isInsurance ? 'Net' : 'Price'}</th><th class="sf-pay-right">Qty</th><th class="sf-pay-right">${isInsurance ? 'Net Total' : 'Total'}</th>
                                </tr>
                            </thead>
                            <tbody>${rows || `<tr><td colspan="${isInsurance ? 8 : 7}" class="sf-pay-empty-row">No parts eligible for return</td></tr>`}</tbody>
                        </table>
                    </div>
                    <div class="sf-pay-actions-row">
                        <button type="button" class="sf-btn sf-btn-secondary" id="sf-pick-back">Back</button>
                        <button type="button" class="sf-btn sf-pay-btn-pay sf-pay-btn-auto" id="sf-pick-continue" ${this.selectedPartIds.size === 0 ? 'disabled' : ''}>Continue</button>
                    </div>
                </div>
            </div>`;

        this.host.querySelectorAll('.sf-return-part-cb').forEach(cb => {
            cb.addEventListener('change', e => {
                const el = e.target as HTMLInputElement;
                const id = Number(el.value);
                if (el.checked) this.selectedPartIds.add(id); else this.selectedPartIds.delete(id);
                const cont = this.host.querySelector('#sf-pick-continue') as HTMLButtonElement;
                if (cont) cont.disabled = this.selectedPartIds.size === 0;
            });
        });
        this.host.querySelector('#sf-pick-back')?.addEventListener('click', () => {
            this.step = 'order';
            this.renderOrder();
        });
        this.host.querySelector('#sf-pick-continue')?.addEventListener('click', () => {
            if (this.selectedPartIds.size === 0) return;
            this.step = 'rmaForm';
            this.rmaFieldValues = null;
            this.loadQuestionsAndRenderRmaForm();
        });

        window.scrollTo(0, 0);
    }

    // ---------- Step: RMA eligibility form ----------

    private async loadQuestionsAndRenderRmaForm(): Promise<void> {
        this.host.innerHTML = `<div class="sf-pay-shell"><div class="sf-loading">Loading return form…</div></div>`;
        try {
            const res = await postJson('/Services/Maintenance/ReturnQuestion/GetAll', {});
            const data = await res.json();
            this.questions = ((data?.Entities || []) as ReturnQuestion[]).slice().sort((a, b) => (a.Order ?? 0) - (b.Order ?? 0));
        } catch {
            this.questions = [];
        }
        this.renderRmaForm();
    }

    private renderRmaForm(error?: string): void {
        const entity = this.entity!;
        const selectedParts = entity.activeParts().filter(p => this.selectedPartIds.has(p.Id));
        const partNames = selectedParts.map(p => p.displayName() || p.PartSku).join(', ');
        const values: RmaFieldValues = this.rmaFieldValues ?? {
            customerName: entity.CustomerName || '',
            phone: entity.CustomerPhone || '',
            email: entity.CustomerEmail || '',
            purchaseDate: toDateInputValue(entity.OrderDate),
            deliveredType: ''
        };

        const questionsHtml = this.questions.map((q, i) => `
            <div class="sf-pay-question-row" id="sf-q${q.Id}">
                <div class="sf-pay-question-text">${i + 1}. ${esc(q.QuestionText)}</div>
                <div class="sf-pay-question-options">
                    ${q.Answers.slice().sort((a, b) => (a.Order ?? 0) - (b.Order ?? 0)).map(a => `
                        <label>
                            <input type="${q.MultiAnswer ? 'checkbox' : 'radio'}" name="sf-q${q.Id}" value="${a.Id}" />
                            ${esc(a.OptionText)}
                        </label>`).join('')}
                </div>
            </div>`).join('');

        this.host.innerHTML = `
            <div class="sf-pay-shell sf-pay-shell-medium">
                <button type="button" class="sf-pay-back-link" id="sf-rma-back" data-action="rma-back">‹ Back</button>
                <div class="sf-pay-heading">
                    <div>
                        <h1>Return Merchandise Authorization</h1>
                        <div class="sf-pay-sub">${esc(entity.OrderId)}</div>
                    </div>
                </div>

                <div class="sf-pay-card">
                    <div class="sf-pay-card-head">Customer &amp; Order Details</div>
                    <div class="sf-pay-form-body">
                        <div class="sf-pay-info-grid">
                            <label class="sf-field">
                                <input type="text" id="rma-customer-name" class="sf-pay-input" value="${esc(values.customerName)}" placeholder=" " required />
                                <span class="sf-field-label">Customer Name <span class="sf-req-star">*</span></span>
                            </label>
                            <label class="sf-field">
                                <input type="text" id="rma-phone" class="sf-pay-input" value="${esc(values.phone)}" placeholder=" " required />
                                <span class="sf-field-label">Phone Number <span class="sf-req-star">*</span></span>
                            </label>
                            <label class="sf-field">
                                <input type="email" id="rma-email" class="sf-pay-input" value="${esc(values.email)}" placeholder=" " required />
                                <span class="sf-field-label">Email <span class="sf-req-star">*</span></span>
                            </label>
                            <label class="sf-field">
                                <input type="text" id="rma-order-number" class="sf-pay-input" value="${esc(entity.OrderId)}" readonly />
                                <span class="sf-field-label">Order Number</span>
                            </label>
                            <label class="sf-field">
                                <input type="date" id="rma-purchase-date" class="sf-pay-input" value="${esc(values.purchaseDate)}" required />
                                <span class="sf-field-label">Date of Purchase <span class="sf-req-star">*</span></span>
                            </label>
                            <div>
                                <div class="sf-pay-label">Type of Item Purchased</div>
                                <div class="sf-pay-value sf-pay-normal">${esc(partNames) || '—'}</div>
                            </div>
                            <div class="sf-pay-full">
                                <div class="sf-pay-label">Was the part delivered to a: <span class="sf-pay-required">*</span></div>
                                <div class="sf-pay-radio-inline">
                                    <label><input type="radio" name="sf-delivered-type" value="2" ${values.deliveredType === '2' ? 'checked' : ''} /> Commercial Address</label>
                                    <label><input type="radio" name="sf-delivered-type" value="4" ${values.deliveredType === '4' ? 'checked' : ''} /> Residential Address</label>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="sf-pay-card">
                    <div class="sf-pay-card-head">
                        Eligibility Questions
                        <div class="sf-pay-sub" style="margin-top:4px;">Answer each of the following questions</div>
                    </div>
                    ${questionsHtml || `<p class="sf-pay-muted sf-pay-form-body">No eligibility questions configured.</p>`}
                </div>

                <div data-bot-slot></div>
                <div class="sf-license-error">${esc(error || '')}</div>

                <div class="sf-rma-actions">
                    <button type="button" class="sf-btn sf-btn-secondary" data-action="rma-back">Back</button>
                    <button type="button" class="sf-btn sf-pay-btn-pay sf-pay-btn-auto" id="sf-rma-submit">Submit RMA</button>
                </div>
            </div>`;

        this.host.querySelectorAll('[data-action="rma-back"]').forEach(b => b.addEventListener('click', () => {
            this.step = 'pickParts';
            this.renderPickParts();
        }));
        this.returnGuard.mount(this.host);
        this.host.querySelector('#sf-rma-submit')?.addEventListener('click', () => this.submitRma());

        window.scrollTo(0, 0);
    }

    private collectAnswers(): { ReturnQuestionId: number; ReturnAnswerOptionId: number }[] | null {
        this.host.querySelectorAll('.sf-pay-question-row').forEach(q => q.classList.remove('unanswered'));

        const answers: { ReturnQuestionId: number; ReturnAnswerOptionId: number }[] = [];
        let allAnswered = true;
        this.questions.forEach(q => {
            const checked = this.host.querySelectorAll(`input[name="sf-q${q.Id}"]:checked`);
            if (checked.length === 0) {
                allAnswered = false;
                this.host.querySelector(`#sf-q${q.Id}`)?.classList.add('unanswered');
                return;
            }
            checked.forEach(el => {
                answers.push({ ReturnQuestionId: q.Id, ReturnAnswerOptionId: Number((el as HTMLInputElement).value) });
            });
        });

        return allAnswered ? answers : null;
    }

    private async submitRma(): Promise<void> {
        const customerName = (this.host.querySelector('#rma-customer-name') as HTMLInputElement)?.value?.trim() || '';
        const phone = (this.host.querySelector('#rma-phone') as HTMLInputElement)?.value?.trim() || '';
        const email = (this.host.querySelector('#rma-email') as HTMLInputElement)?.value?.trim() || '';
        const purchaseDate = (this.host.querySelector('#rma-purchase-date') as HTMLInputElement)?.value || '';
        const deliveredTypeInput = this.host.querySelector('input[name="sf-delivered-type"]:checked') as HTMLInputElement;

        this.rmaFieldValues = { customerName, phone, email, purchaseDate, deliveredType: deliveredTypeInput?.value || '' };

        if (!customerName || !phone || !email || !purchaseDate || !deliveredTypeInput) {
            this.renderRmaForm('Please fill in all required fields.');
            return;
        }

        const answers = this.collectAnswers();
        if (!answers) {
            this.renderRmaForm('Please answer all questions.');
            return;
        }

        const confirmed = await showConfirmModal({
            title: 'Submit this RMA request?',
            message: 'Please confirm all information and eligibility answers are correct. Once submitted, this request will be sent for review.',
            confirmLabel: 'Confirm & Submit'
        });
        if (!confirmed) return;

        const entity = this.entity!;
        const deliveredType = Number(deliveredTypeInput.value);
        const selectedParts = entity.activeParts().filter(p => this.selectedPartIds.has(p.Id));

        const payload = {
            Entity: {
                OrderId: entity.Id,
                CustomerName: customerName,
                PhoneNumber: phone,
                Email: email,
                OrderNumber: entity.OrderId,
                PurchaseDate: purchaseDate,
                DeliveredAddressType: deliveredType,
                Parts: selectedParts.map(p => ({ OrderPartId: p.Id, PartId: p.PartId })),
                Answers: answers
            }
        };

        const btn = this.host.querySelector('#sf-rma-submit') as HTMLButtonElement;
        if (btn) { btn.disabled = true; btn.textContent = 'Submitting…'; }

        try {
            const res = await postGuarded('/Services/Public/Return/Create', payload, this.returnGuard);
            this.returnGuard.reset();
            const data = await res.json();
            if (!res.ok) {
                this.renderRmaForm(errorMessage(data) || 'Could not submit your return. Please try again.');
                return;
            }
            const status = data?.CustomData?.Status;
            const message = data?.CustomData?.Message ||
                (status === 2 ? 'Your return request has been submitted.' : 'Your return request could not be processed.');
            this.renderRmaResult(status === 2, message, data?.CustomData?.ReturnNumber);
        } catch {
            this.returnGuard.reset();
            this.renderRmaForm('Could not submit your return. Please try again.');
        }
    }

    private renderRmaResult(success: boolean, message: string, returnNumber?: string): void {
        this.host.innerHTML = `
            <div class="sf-pay-shell sf-pay-shell-narrow">
                <div class="sf-empty">
                    <h2>${success ? 'Return Submitted' : 'Return Not Approved'}</h2>
                    ${returnNumber ? `<p>Your RMA number is <strong>${esc(returnNumber)}</strong>. Please keep it for your records.</p>` : ''}
                    <p>${esc(message)}</p>
                    <a href="${this.directOrderId ? sfUrl('/account') : '/Public/LookupOrder'}" class="sf-btn">Done</a>
                </div>
            </div>`;
        window.scrollTo(0, 0);
    }
}

function init(): void {
    const host = document.getElementById('lookup-root');
    if (!host) return;
    const raw = host.dataset.orderId;
    const orderId = raw ? Number(raw) : NaN;
    useOrderToken(host.dataset.token);
    const startReturn = new URLSearchParams(window.location.search).get('return') === '1';
    new LookupOrderView(host, !isNaN(orderId) && orderId > 0 ? orderId : null, host.dataset.expired === '1', startReturn);
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
} else {
    init();
}
